/**
 * Pure presentation helpers for the deliveries drawer's Sub-flows
 * section (ADR-0008 PR3b2). Extracted from DeliveriesPage so the
 * gating + tinting logic is unit-testable without mounting the page.
 *
 * These encode privilege- and contract-sensitive rules:
 *   - ``canFireBranch`` decides whether the manual "Fire" button shows.
 *     Getting it wrong = a privilege leak (non-admin sees Fire) or a
 *     guaranteed backend rejection (firing an auto-only branch).
 *   - ``outcomeTone`` maps the classified outcome to a tint class.
 */
import type { DeliveryBranchSnapshot } from "@/lib/api-deliveries";


/** Whether the operator may manually fire this branch.
 *
 *  All three must hold (mirrors the backend's dispatch_for_action
 *  guards):
 *    1. the caller is an admin (privilege gate);
 *    2. the branch trigger is ``manual`` or ``both`` (auto-only fires
 *       from the drain, never from a click — backend 400s otherwise);
 *    3. the branch has a linked sub-flow workflow to start.
 */
export function canFireBranch(
  branch: Pick<DeliveryBranchSnapshot, "trigger" | "linked_workflow_id">,
  canExecuteAction: boolean,
): boolean {
  if (!canExecuteAction) return false;
  if (branch.trigger !== "manual" && branch.trigger !== "both") return false;
  return !!branch.linked_workflow_id;
}


/** Tailwind tint classes for the terminal outcome chip. ``null`` /
 *  unknown → empty string (chip not rendered while in flight). Custom
 *  branch ids (anything that isn't succeeded / dead_letter) get the
 *  violet "routed" tint. */
export function outcomeTone(outcome: string | null | undefined): string {
  if (outcome === "succeeded") {
    return "border-emerald-400/40 bg-emerald-400/10 text-emerald-300";
  }
  if (outcome === "dead_letter") {
    return "border-rose-400/40 bg-rose-400/10 text-rose-300";
  }
  if (outcome) {
    return "border-violet-400/40 bg-violet-400/10 text-violet-300";
  }
  return "";
}
